import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Minimalist back link — small left-pointing filled triangle + label.
 * Uses `text-link` so back-nav pops consistently across screens
 * (Send / Receive / …). The triangle glyph matches the Settings
 * collapsible chevron for a shared visual language.
 *
 * Default destination is -1 (browser history back). Pass `to` to force
 * a specific route (e.g. `/wallet` so the button doesn't surprise-escape
 * to an unrelated screen when the stack is shallow).
 */
export function BackLink({
  to,
  label,
  className = '',
}: {
  readonly to?: string | -1;
  readonly label: string;
  readonly className?: string;
}): ReactNode {
  const navigate = useNavigate();
  const go = () => {
    if (to === undefined || to === -1) {
      navigate(-1);
    } else {
      navigate(to);
    }
  };
  return (
    <button
      type="button"
      onClick={go}
      aria-label={label}
      className={[
        '-ml-1 flex w-fit items-center gap-1.5 rounded-md px-1.5 py-1 text-[13px] font-medium text-link transition-colors hover:text-text-primary',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        fill="currentColor"
        aria-hidden
      >
        <path d="M7 1L3 5l4 4z" />
      </svg>
      {label}
    </button>
  );
}
